import React, { useCallback } from 'react';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { useClusterResponses } from '@mentimeter/core-hooks';
import type { ResponseCluster, ResponseText } from '@mentimeter/http-clients';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { GroupShapeMap } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { useTranslations } from 'next-intl';
import { trackUser } from '@api/tracking/client';
import { UNGROUPED_CLUSTER_ID } from '../../../groups';
import type { UpdatedGroupingTrackingPayload } from '../utils/types';
import { useOpenSlideState } from '../../utils/useOpenSlideState';
import { useAssignedClusterStore } from '../utils/useAssignedClusterStore';
import { GroupDetailView } from './GroupDetailView';
import { DetailViewNavigation } from './DetailViewNavigation';

type HandleAssignResponseToGroup = (
  targetGroupId: string,
  response: ResponseText,
) => void;

type HandleCreateNewGroup = (response: ResponseText) => void;

interface GroupDetailViewDataHandlerProps {
  groups: ResponseCluster[];
  shouldShowVotes: boolean;
  clusteringId: string | undefined;
  groupShapeMap: GroupShapeMap;
}

export const GroupDetailViewDataHandler = ({
  groups,
  shouldShowVotes,
  clusteringId,
  groupShapeMap,
}: GroupDetailViewDataHandlerProps) => {
  const { useQuestion, useConfig, useTestVotes } = usePresentationContext();
  const { admin_key, series_id, layout } = useQuestion();
  const { readonly } = useConfig();
  const { testVotesShowing } = useTestVotes();

  const translate = useTranslations('presenting.legacy');
  const [{ activeClusterId }, setSlideState] = useOpenSlideState();

  const { assignResponseToCluster, createNewCluster, updateResponseCluster } =
    useClusterResponses(admin_key);

  const handleCloseGroupDetailView = useCallback(() => {
    setSlideState({ activeClusterId: null });
  }, [setSlideState]);

  const handleGroupTitleUpdate = useCallback(
    (groupId: string, title: string) => {
      const group = groups.find((group) => group.id === groupId);
      if (!group) {
        captureException(
          new MentiError(
            "Tried to update group title but the group id doesn't exist.",
            {
              feature: 'slides',
            },
          ),
        );
        return;
      }
      updateResponseCluster({
        cluster: { ...group, label: title },
      });

      trackUser({
        event: 'Updated open ended clustering',
        properties: {
          context: 'Presentation view',
          update: 'Manual label',
          'series id': series_id ?? null,
          'question state': 'Results view',
        },
      } satisfies UpdatedGroupingTrackingPayload);
    },
    [groups, updateResponseCluster, series_id],
  );

  const {
    setShouldShow: setShowEditNotification,
    setTargetClusterName: setNotificationGroupName,
  } = useAssignedClusterStore((state) => state);

  const handleAssignResponseToGroup: HandleAssignResponseToGroup = useCallback(
    (targetGroupId, response) => {
      const updatedResponse = {
        ...response,
        cluster_id: targetGroupId,
      };
      const targetGroup = groups.find((group) => group.id === targetGroupId);
      if (!targetGroup) {
        captureException(
          new MentiError('Could not find group ID', {
            feature: 'slides',
          }),
        );
        return;
      }

      const updatedTargetGroup = {
        ...targetGroup,
        responses: [...targetGroup.responses, updatedResponse],
      };
      assignResponseToCluster({
        updatedTargetCluster: updatedTargetGroup,
        assignedResponseId: response.id,
      });
      trackUser({
        event: 'Updated open ended clustering',
        properties: {
          context: 'Presentation view',
          update: 'Assigned response',
          'series id': series_id ?? null,
          'question state': 'Results view',
        },
      } satisfies UpdatedGroupingTrackingPayload);
      setNotificationGroupName(updatedTargetGroup.label);
      setShowEditNotification(true);
    },
    [
      assignResponseToCluster,
      groups,
      series_id,
      setShowEditNotification,
      setNotificationGroupName,
    ],
  );

  const handleCreateNewGroup: HandleCreateNewGroup = useCallback(
    async (response) => {
      if (!clusteringId) return;
      const newGroup = await createNewCluster({
        clusteringId,
        clusterLabel: translate('clustering.new_group'),
      });
      if (!newGroup) {
        captureException(
          new MentiError('Failed to create new group', {
            feature: 'slides',
          }),
        );
        return;
      }
      assignResponseToCluster({
        updatedTargetCluster: newGroup,
        assignedResponseId: response.id,
      });
      trackUser({
        event: 'Updated open ended clustering',
        properties: {
          context: 'Presentation view',
          update: 'Assigned response to new cluster',
          'series id': series_id ?? null,
          'question state': 'Results view',
        },
      } satisfies UpdatedGroupingTrackingPayload);
      setNotificationGroupName(newGroup.label);
      setShowEditNotification(true);
    },
    [
      assignResponseToCluster,
      createNewCluster,
      clusteringId,
      series_id,
      setShowEditNotification,
      setNotificationGroupName,
      translate,
    ],
  );

  const moderationOptions = groups
    .filter(({ id }) => id !== UNGROUPED_CLUSTER_ID)
    .map(({ id, label }) => ({
      id,
      label,
      shape: groupShapeMap.get(id),
    }));

  const handleTitleUpdate = useCallback(
    (newTitle: string) => {
      if (!activeClusterId) {
        return;
      }
      handleGroupTitleUpdate(activeClusterId, newTitle);
    },
    [activeClusterId, handleGroupTitleUpdate],
  );

  const handleNavigateGroup = useCallback(
    (direction: 'next' | 'previous') => {
      if (!activeClusterId) {
        return;
      }
      const currentIndex = groups.findIndex(
        (group) => group && group.id === activeClusterId,
      );
      const nextIndex =
        direction === 'next' ? currentIndex + 1 : currentIndex - 1;
      const nextGroup = groups[nextIndex];
      if (nextGroup) {
        setSlideState({ activeClusterId: nextGroup.id });
      }
    },
    [groups, activeClusterId, setSlideState],
  );

  const activeGroup =
    activeClusterId && groups.find((group) => group.id === activeClusterId);

  if (!activeGroup) {
    return null;
  }
  const isFirstGroup = groups[0]?.id === activeGroup.id;
  const isLastGroup = groups[groups.length - 1]?.id === activeGroup.id;

  const maxGroupVoteCount = Math.max(
    ...groups.map((group) => group?.total_upvotes ?? 0),
  );

  const isUngroupedGroup = activeGroup.id === UNGROUPED_CLUSTER_ID;
  const shouldShowGroupTag = !shouldShowVotes && isUngroupedGroup;
  const hasSideImageLayout =
    layout?.type === 'right' || layout?.type === 'left';
  const numberOfColumns = hasSideImageLayout ? 2 : 3;

  const noOp = () => {};

  return (
    <GroupDetailView
      onClose={handleCloseGroupDetailView}
      shouldShowVotes={shouldShowVotes}
      id={activeGroup.id}
      totalVotes={activeGroup.total_upvotes}
      title={activeGroup.label}
      responses={activeGroup.responses}
      isTopVoted={
        activeGroup.total_upvotes === maxGroupVoteCount &&
        activeGroup.total_upvotes > 0
      }
      hasUncategorizedTag={shouldShowGroupTag}
      shape={groupShapeMap.get(activeGroup.id)}
      onTitleUpdate={testVotesShowing ? noOp : handleTitleUpdate}
      moderationOptions={moderationOptions}
      onCreateNewGroup={testVotesShowing ? noOp : handleCreateNewGroup}
      onAssignToGroup={testVotesShowing ? noOp : handleAssignResponseToGroup}
      numberOfColumns={numberOfColumns}
      navigation={
        readonly ? null : (
          <DetailViewNavigation
            onNavigate={handleNavigateGroup}
            canNavigateNext={!isLastGroup}
            canNavigatePrevious={!isFirstGroup}
          />
        )
      }
    />
  );
};
